"use client";

import { config } from "@/shared/lib/config";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookupForInfinite, type LookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/shared/ui/DatePicker";

import { useState } from "react";
import type { WalletParams } from "../../api/client";

type WalletReportsFilterProps = {
  filters: WalletParams;
  onFiltersChange: (filters: WalletParams) => void;
};

type CustomerLookupItem = {
  id: string;
  name: string | null;
  phone: string;
};

export function WalletReportsFilter({
  filters,
  onFiltersChange,
}: WalletReportsFilterProps) {
  const [fromDate, setFromDate] = useState<string>(
    filters.fromDate ? formatDateForInput(filters.fromDate) : "",
  );
  const [toDate, setToDate] = useState<string>(
    filters.toDate ? formatDateForInput(filters.toDate) : "",
  );
  const [transactionType, setTransactionType] = useState<string>(
    filters.transactionTypes && filters.transactionTypes.length > 0
      ? filters.transactionTypes.join(",")
      : "all",
  );
  const [status, setStatus] = useState<string>(filters.status || "all");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    filters.customerId || "",
  );

  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[],
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;

    const fromStr = from ? formatDateForInput(from) : "";
    const toStr = to ? formatDateForInput(to) : "";

    setFromDate(fromStr);
    setToDate(toStr);

    const fromDateObj = from
      ? new Date(new Date(from).setHours(0, 0, 0, 0))
      : undefined;
    const toDateObj = to
      ? new Date(new Date(to).setHours(23, 59, 59, 999))
      : undefined;

    onFiltersChange({
      ...filters,
      fromDate: fromDateObj,
      toDate: toDateObj,
    });
  };

  const handleTransactionTypeChange = (value: string) => {
    setTransactionType(value);
    const types =
      value === "all"
        ? undefined
        : (value.split(",") as (
            | "TOPUP"
            | "PAYMENT"
            | "REFUND"
            | "ADJUSTMENT"
          )[]);
    onFiltersChange({
      ...filters,
      transactionTypes: types,
    });
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    const newStatus =
      value === "all"
        ? undefined
        : (value as "PENDING" | "APPROVED" | "POSTED" | "REJECTED");
    onFiltersChange({
      ...filters,
      status: newStatus,
    });
  };

  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);
    onFiltersChange({
      ...filters,
      customerId: customerId || undefined,
    });
  };

  const handleReset = () => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);

    setFromDate(formatDateForInput(fromDate));
    setToDate(formatDateForInput(toDate));
    setTransactionType("all");
    setStatus("all");
    setSelectedCustomerId("");

    onFiltersChange({
      fromDate,
      toDate,
    });
  };

  const hasFilters =
    fromDate ||
    toDate ||
    transactionType !== "all" ||
    status !== "all" ||
    selectedCustomerId;

  // Customer lookup function
  const customersLookupFn = async (args: {
    search: string;
    pageParam: number;
  }) => {
    return fetchLookupForInfinite<LookupItem>(
      `${config.apiUrl}/customers/lookup`,
      args,
    );
  };

  const preloadCustomerFn = async (
    id: string,
  ): Promise<CustomerLookupItem | null> => {
    try {
      const data = await fetcher.get<{ item: CustomerLookupItem | null }>(
        `${config.apiUrl}/customers/lookup/${encodeURIComponent(id)}`,
      );
      return data.item;
    } catch {
      return null;
    }
  };

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-4">
      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range */}
        <DatePicker
          mode="range"
          value={{
            from: fromDate ? parseISO(fromDate) : undefined,
            to: toDate ? parseISO(toDate) : undefined,
          }}
          onChange={(date) => {
            if (date) {
              handleDateRangeChange(date);
            }
          }}
          placeholder="ຊ່ວງວັນທີ"
          className="h-9 w-full sm:w-fit"
        />

        {/* Transaction Type Select */}
        <SimpleSelect
          placeholder="ປະເພດທຸລະກຳ"
          value={transactionType}
          onValueChange={handleTransactionTypeChange}
          className="h-9 w-full sm:w-48"
          options={[
            { value: "all", label: "ທັງໝົດ" },
            { value: "TOPUP", label: "ເຕີມເງິນ" },
            { value: "PAYMENT", label: "ຈ່າຍ" },
            { value: "REFUND", label: "ຄືນເງິນ" },
            { value: "ADJUSTMENT", label: "ປັບປຸງ" },
          ]}
        />

        {/* Status Select */}
        <SimpleSelect
          placeholder="ສະຖານະ"
          value={status}
          onValueChange={handleStatusChange}
          className="h-9 w-full sm:w-40"
          options={[
            { value: "all", label: "ທັງໝົດ" },
            { value: "PENDING", label: "ລໍຖ້າ" },
            { value: "APPROVED", label: "ອະນຸມັດ" },
            { value: "POSTED", label: "ບັນທຶກແລ້ວ" },
            { value: "REJECTED", label: "ປະຕິເສດ" },
          ]}
        />

        {/* Customer Select */}
        <InfiniteCombobox<CustomerLookupItem>
          queryKey={["customers", "lookup"]}
          queryFn={customersLookupFn}
          preloadQueryFn={preloadCustomerFn}
          getLabel={(item) => item.name || item.phone}
          getValue={(item) => item.id}
          value={selectedCustomerId}
          onValueChange={handleCustomerChange}
          placeholder="ເລືອກລູກຄ້າ..."
          className="h-9 w-full sm:w-64"
          clearable
        />

        {/* Reset Button */}
        {hasFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="h-9"
          >
            ລ້າງການກອງ
          </Button>
        )}
      </div>
    </div>
  );
}
